/*
 * The Private License
 *
 * Copyright (c) 2023 Mahon Media LLC.
 */
import React, {useEffect, useMemo, useRef, useState} from "react";
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import {createCsv, createUniqueCodeItems} from "../utils/compilers";

import CustomMultipleSelect from "../components/CustomMultipleSelect";
import Cookies from "js-cookie";
import {useNotification} from "../contexts/NotificationContext";
import {useCustomers} from "../contexts/CustomersContext";
import UpgradeRoom from "./UpgradeRoom";
import {enoughAvailability, possible} from "../utils/enoughAvailability";
import { getBackendBaseUrl } from "../utils/envUtils";
import { CheckBox, CheckBoxOutlineBlank } from "@mui/icons-material";

function ReservationData({data, rooms, from, to, env, rates}) {
  const {setIsOpen, setIsFailed, setMessage} = useNotification();
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = React.useState(null);
  const upgradeRf = useRef(null);
  const [isMounted, setIsMounted] = useState(false);
  useEffect(() => {
    setIsMounted(true);
  }, []);

  const [upsellTo, setUpsellTo] = useState(
    data.upsellTo.roomTypes.map(pickedRoom => {
      const roomDetails = rooms.find(type => type.code === pickedRoom.roomType);
      if (roomDetails) {
        return {
          roomType: pickedRoom.roomType,
          friendly: roomDetails.friendly,
          description: roomDetails.description,
          image: roomDetails.image,
          usedForUpsell: roomDetails.usedForUpsell,
          requiredAvailable: roomDetails.requiredAvailable,
          allowOversoldUpTo: roomDetails.allowOversoldUpTo
        };
      }
      return null;
    }).filter(details => details !== null)
  );

  const roomTypeMap = upsellTo.reduce((map, room) => {
    map[room.roomType] = room;
    return map;
  }, {});

  const availability = data.upsellTo.availability.available.map(entry => {
    const extended = roomTypeMap[entry.roomType];
    return {
      ...entry,
      ...extended
    };
  });
  const [showCustomers, setShowCustomers] = useState(false);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [isFilterOn, setIsFilterOn] = useState(false);
  const [selectedCode, setSelectedCode] = useState([]);
  const {customers, setCustomers} = useCustomers();
  const [checkedState, setCheckedState] = useState(
    new Array(
      customers.length
    ).fill(false)
  );
  const [selectedCustomers, setSelectedCustomers] = useState([]);
  const [upgrade, setUpgrade] = useState(false);
  const token = Cookies.get("jwtToken");


  const scrollToUpgradeRoom = () => {
    upgradeRf.current.scrollIntoView({behavior: 'smooth'});
  };

  const handleSelectCode = (e) => {
    const value = e.target.value;

    if (value[value.length - 1] === "all") {
      setSelectedCode(
        selectedCode.length ===
        createUniqueCodeItems(data.upsellFrom.customers).length
          ? []
          : createUniqueCodeItems(data.upsellFrom.customers)
      );
      setIsFilterOn(false);
      setFilteredCustomers([]);
      return;
    }
    setIsFilterOn(true);
    setFilteredCustomers(
      customers.filter((customer) =>
        customer.bookingChannelCode
          ? value.includes(customer.bookingChannelCode)
          : value.includes("None")
      )
    );
    setSelectedCode(value);
  };

  const toggleCustomerList = () => {
    setShowCustomers(!showCustomers);
  };

  const handleCsvDownload = () => {
    const csvData = isFilterOn
      ? createCsv(filteredCustomers)
      : createCsv(
        customers
      );
    const blob = new Blob([csvData], {type: "text/csv"});
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "customers.csv";
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
  };
  
  const handleSelectAllCustomers = () => {
    setCheckedState(new Array(customers.length).fill(true));
    setSelectedCustomers([...customers]);
  };
  const handleClearSelectedCustomers = () => {
    setCheckedState(new Array(customers.length).fill(false));
    setSelectedCustomers([]);
  };

  const handleOnChange = (index) => {
    const updatedCheckedState = checkedState.map((item, idx) =>
      idx === index ? !item : item
    );
    setCheckedState(updatedCheckedState);
    const updatedSelectedCustomers = updatedCheckedState
      .map((checked, idx) =>
        checked ?
          customers[idx]
          : null
      ).filter(customer => customer !== null);
    setSelectedCustomers(updatedSelectedCustomers);
  };

  const handleUpgrade = () => {
    setUpgrade(true);
    scrollToUpgradeRoom();
  };

  useEffect(() => {
    if (upgrade && upgradeRf.current) {
      scrollToUpgradeRoom();
    }
  }, [upgrade]);

  const handleCancel = () => {
    setUpgrade(false);
  };

  const handleConfirm = (mapped) => {
    setLoading(true);
    mapped.forEach((guest) => {
      let rid = guest.rid.trim();
      let target;
      Object.entries(guest.prices).forEach(([room, value]) => {
        if (value === "0") {
          target = room;
        }
      });
      const host = getBackendBaseUrl();
      const url = `${host}/api/v1/reservations/${rid}?env=${env}&roomType=${target}`;
      if (guest.prices !== undefined) {
        if (target !== undefined) {
          if (enoughAvailability(availability)) {
            fetch(url, {
              method: 'PUT',
              headers: {
                Authorization: `Bearer ${token}`,
              }
            }).then(async (response) => {
              if (response.ok) {
                setIsOpen(true);
                setIsFailed(false);
                setMessage(
                  `Reservation ${rid} upgraded to ${target}`
                );
                const reservations = `${host}/api/v1/inventory-reservations?${from}&${to}&startDate=${data.startDate}&endDate=${data.endDate}&env=${env}&${rates}`;
                fetch(reservations, {
                  headers: {
                    Authorization: `Bearer ${token}`,
                  },
                }).then((rs) => {
                  rs.json().then(
                    (fresh) => {
                      setCustomers(fresh.upsellFrom.customers.filter(customer => customer.email &&
                        (!["", "none"].includes(customer.email.trim().toLowerCase()))
                      ));
                      setCheckedState(
                        new Array(
                          customers.length
                        ).fill(false)
                      );
                      setLoading(false);
                      setSelectedCustomers([]);
                      setUpgrade(false);
                    }
                  );
                });
              } else {
                if (response.status === 400) {
                  await response.json().then((err) => {
                    throw new Error(
                      `Can not upgrade to ${target}, due to: ${err.message}`
                    )
                  });
                }
              }
            }).catch((err) => {
              setIsOpen(true);
              setIsFailed(true);
              setMessage(err.message);
              setLoading(false);
            });
          } else {
            setIsOpen(true);
            setIsFailed(true);
            setMessage(
              `Can not upgrade to ${target}, due to: not enough inventory for upsell`
            );
            setLoading(false);
          }
        }
      }
    });
  };

  let sortedCustomers = isFilterOn ? [...filteredCustomers] :
    [...customers];
  if (sortConfig !== null) {
    sortedCustomers.sort((a, b) => {
      if (a[sortConfig.key] === undefined && b[sortConfig.key] === undefined) {
        return 0;
      }
      if (a[sortConfig.key] === undefined) {
        return 1;
      }
      if (b[sortConfig.key] === undefined) {
        return -1;
      }
      if (a[sortConfig.key] < b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? -1 : 1;
      }
      if (a[sortConfig.key] > b[sortConfig.key]) {
        return sortConfig.direction === 'ascending' ? 1 : -1;
      }
      return 0;
    });
  }

  const requestSort = key => {
    let direction = 'ascending';
    if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({key, direction});
  };

  const selectedUpsellTo = (selected, types) => {
    return selected.map(pickedRoom => {
      const roomDetails = types.find(type => type.code === pickedRoom.roomType);
      if (roomDetails) {
        return {
          roomType: pickedRoom.roomType,
          friendly: roomDetails.friendly,
          description: roomDetails.description,
          image: roomDetails.image,
          usedForUpsell: roomDetails.usedForUpsell,
          requiredAvailable: roomDetails.requiredAvailable,
          allowOversoldUpTo: roomDetails.allowOversoldUpTo
        };
      }
      return null;
    }).filter(details => details !== null);
  };

  return (
    <>
      <Paper
        elevation={3}
        style={{
          padding: "20px",
          fontFamily: "Monaco, sans-serif",
          fontSize: "9px",
          display: "flex",
          flexDirection: `${showCustomers ? "column" : "row"}`,
          justifyContent: "space-between",
          marginBottom: "40px",
          width: "110%"
        }}
      >
        <div style={{flex: "1"}}>
          <Typography variant="h5" sx={{fontSize: "14px"}} gutterBottom>
            Upsell from {data.startDate} to {data.endDate}
          </Typography>
          <Typography variant="h6" sx={{fontSize: "14px"}} gutterBottom>
            Upsell From: {data.upsellFrom.roomTypes.map((room) => `${room.roomType} `).join("")}
          </Typography>
          <Typography variant="body1" sx={{fontSize: "14px"}} gutterBottom>
            Total Count: {data.upsellFrom.totalCount}
          </Typography>
          <Button onClick={toggleCustomerList} color="primary">
            {showCustomers ? "Hide Customers" : "Show Customers"}
          </Button>
          {showCustomers && (
            <>
              <Typography variant="h6" sx={{fontSize: "14px"}} gutterBottom>
                Customers:
              </Typography>
              <Button
                variant={"contained"}
                color="primary"
                onClick={handleCsvDownload}
                disabled={customers.length === 0}
              >
                Download CSV
              </Button>
              <ul
                style={{
                  display: "flex",
                  flexDirection: "column",
                  padding: "0",
                  marginRight: "20px",
                }}
              >
                <CustomMultipleSelect
                  label="Booking Channel Code Filter"
                  labelId="booking-channel-code"
                  options={createUniqueCodeItems(customers)}
                  selectedValue={selectedCode}
                  setSelectedValue={setSelectedCode}
                  handleSelectValue={handleSelectCode}
                />
                <div style={{ flex: "12"}}>
                  <div style={{flex: "1", display: 'flex', justifyContent: 'flex-end'}}>
                    <Button style={{textTransform: 'none', fontSize: '0.8rem', padding: '6px 10px', margin: '4px 4px 0 0'}}
                          onClick={() => handleSelectAllCustomers()}
                          variant={"contained"}
                          startIcon={<CheckBox />}
                          >
                      Check All
                    </Button>
                    <Button style={{textTransform: 'none', fontSize: '0.8rem', padding: '6px 10px', margin: '4px 4px 0 0'}}
                            onClick={() => handleClearSelectedCustomers()}
                            variant={"contained"}
                            startIcon={<CheckBoxOutlineBlank />}
                            >
                      Uncheck All
                    </Button>
                  </div>
                </div>
                
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>
                          Confirmation Number
                        </TableCell>
                        <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>
                          <Button style={{textTransform: 'none', fontSize: '0.8rem', padding: '6px 10px',}}
                                  onClick={() => requestSort('name')}>
                            Name
                          </Button>
                        </TableCell>
                        <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>
                          <Button style={{textTransform: 'none', fontSize: '0.8rem', padding: '6px 10px',}}
                                  onClick={() => requestSort('surname')}>
                            Surname
                          </Button>
                        </TableCell>
                        <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>
                          <Button style={{textTransform: 'none', fontSize: '0.8rem', padding: '6px 10px',}}
                                  onClick={() => requestSort('email')}>
                            Email
                          </Button>
                        </TableCell>
                        <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>
                          <Button style={{textTransform: 'none', fontSize: '0.8rem', padding: '6px 10px',}}
                                  onClick={() => requestSort('roomType')}>
                            Room Type
                          </Button>
                        </TableCell>
                        <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>
                          <Button style={{textTransform: 'none', fontSize: '0.8rem', padding: '6px 10px',}}
                                  onClick={() => requestSort('ratePlan')}>
                            Rate Plan
                          </Button>
                        </TableCell>
                        <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>
                          <Button style={{textTransform: 'none', fontSize: '0.8rem', padding: '6px 10px',}}
                                  onClick={() => requestSort('rateAmount')}>
                            Rate Amount
                          </Button>
                        </TableCell>
                        <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>
                          <Button style={{textTransform: 'none', fontSize: '0.8rem', padding: '6px 10px',}}
                                  onClick={() => requestSort('checkIn')}>
                            Check In
                          </Button>
                        </TableCell>
                        <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>
                          <Button style={{textTransform: 'none', fontSize: '0.8rem', padding: '6px 10px',}}
                                  onClick={() => requestSort('checkOut')}>
                            Check Out
                          </Button>
                        </TableCell>
                        <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>
                          <Button style={{textTransform: 'none', fontSize: '0.8rem', padding: '6px 10px',}}
                                  onClick={() => requestSort('bookingChannelCode')}>
                            Booking Channel Code
                          </Button>
                        </TableCell>
                        <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>For Upgrade</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sortedCustomers.map((customer, index) => (
                        <TableRow key={index}>
                          <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>{customer.cid}</TableCell>
                          <TableCell
                            style={{fontSize: '0.8rem', padding: '6px 10px',}}>{customer.name || "None"}</TableCell>
                          <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>{customer.surname}</TableCell>
                          <TableCell
                            style={{fontSize: '0.8rem', padding: '6px 10px',}}>{customer.email || "None"}</TableCell>
                          <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>{customer.roomType}</TableCell>
                          <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>{customer.ratePlan}</TableCell>
                          <TableCell
                            style={{fontSize: '0.8rem', padding: '6px 10px',}}>{customer.rateAmount}</TableCell>
                          <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>{customer.checkIn}</TableCell>
                          <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>{customer.checkOut}</TableCell>
                          <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>
                            {customer.bookingChannelCode || "None"}
                          </TableCell>
                          <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>
                            <input
                              type="checkbox"
                              onChange={() => handleOnChange(index)}
                              checked={checkedState[index]}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <Button
                  variant={"contained"}
                  color="primary"
                  onClick={handleUpgrade}>
                  Upgrade
                </Button>
              </ul>
            </>
          )}
        </div>

        <div style={{flex: "1"}}>
          <Typography variant="h6" sx={{fontSize: "14px"}} gutterBottom>
            Upsell To: {upsellTo.map((room) => `${room.roomType} `).join("")}
          </Typography>
          <Typography variant="h6" sx={{fontSize: "14px"}} gutterBottom>
            Availability per day:
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>Room Type</TableCell>
                  <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>Required Available</TableCell>
                  <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>Allow Oversold Up To</TableCell>
                  <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>Available Count</TableCell>
                  <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>Start Date</TableCell>
                  <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>End Date</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {availability.map((inventory, index) => (
                  <TableRow key={index}>
                    <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>{inventory.roomType}</TableCell>
                    <TableCell
                      style={{fontSize: '0.8rem', padding: '6px 10px',}}>{inventory.requiredAvailable}</TableCell>
                    <TableCell
                      style={{fontSize: '0.8rem', padding: '6px 10px',}}>{inventory.allowOversoldUpTo}</TableCell>
                    <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>{inventory.availableCount}</TableCell>
                    <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>{inventory.startDate}</TableCell>
                    <TableCell style={{fontSize: '0.8rem', padding: '6px 10px',}}>{inventory.endDate}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </Paper>
      <Paper>
        {
          upgrade ? (
            <UpgradeRoom
              ref={upgradeRf}
              selected={selectedCustomers}
              from={data.upsellFrom.roomType}
              to={data.upsellTo.roomTypes}
              env={env}
              confirm={handleConfirm}
              cancel={handleCancel}
              loading={loading}
              availability={availability}
            >
            </UpgradeRoom>
          ) : null
        }
      </Paper>
    </>
  );
}

export default ReservationData;
